import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, Trash2, Truck } from "lucide-react";
import axios from "axios";
import { useState } from "react";
import { useCart, COMING_SOON } from "@/context/CartContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CartDrawer = () => {
    const { items, open, setOpen, setQty, remove, subtotal } = useCart();
    const [checkingOut, setCheckingOut] = useState(false);
    const [termsViewed, setTermsViewed] = useState(false);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const hasLiveAnimal = items.some((i) => !["terrariums", "caresheet", "caresheets"].includes(i.product.category));

    const checkout = async () => {
        setCheckingOut(true);
        try {
            const { data } = await axios.post(`${API}/payments/checkout`, {
                items: items.map((i) => ({ product_id: i.product.id, quantity: i.qty })),
                origin_url: window.location.origin,
            });
            window.location.href = data.checkout_url;
        } catch (e) {
            const detail = e.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Checkout failed — please try again");
            setCheckingOut(false);
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm"
                        onClick={() => setOpen(false)}
                    />
                    <motion.aside
                        initial={{ x: "100%" }}
                        animate={{ x: 0 }}
                        exit={{ x: "100%" }}
                        transition={{ type: "spring", stiffness: 260, damping: 30 }}
                        data-testid="cart-drawer"
                        className="fixed right-0 top-0 z-[75] flex h-full w-full max-w-md flex-col border-l border-red-900/50 bg-coal"
                    >
                        <div className="flex items-center justify-between border-b border-red-950/50 p-6">
                            <h2 className="font-display text-lg font-bold text-bone">Your Enclosure</h2>
                            <button
                                data-testid="cart-close-button"
                                onClick={() => setOpen(false)}
                                className="text-ash transition-colors duration-300 hover:text-bone"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6">
                            {items.length === 0 ? (
                                <p className="mt-10 text-center font-editorial text-lg italic text-ash" data-testid="cart-empty-message">
                                    The enclosure is empty. Something wild awaits.
                                </p>
                            ) : (
                                <ul className="space-y-5">
                                    {items.map((i) => (
                                        <li
                                            key={i.product.id}
                                            data-testid="cart-item"
                                            className="flex gap-4 border border-red-950/40 bg-black/30 p-3"
                                        >
                                            <img
                                                src={i.product.image_url}
                                                alt={i.product.name}
                                                className="h-20 w-20 object-cover"
                                            />
                                            <div className="flex flex-1 flex-col">
                                                <div className="flex items-start justify-between">
                                                    <div>
                                                        <p className="font-display text-sm font-bold text-bone">
                                                            {i.product.name}
                                                        </p>
                                                        <p className="font-mono2 text-[10px] uppercase tracking-[0.15em] text-ash">
                                                            ${i.product.price.toLocaleString()} each
                                                        </p>
                                                    </div>
                                                    <button
                                                        data-testid="cart-item-remove"
                                                        onClick={() => remove(i.product.id)}
                                                        className="text-ash transition-colors duration-300 hover:text-red-400"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </button>
                                                </div>
                                                <div className="mt-auto flex items-center gap-3 pt-2">
                                                    <button
                                                        data-testid="cart-item-decrease"
                                                        onClick={() => setQty(i.product.id, i.qty - 1)}
                                                        className="border border-red-950/60 p-1 text-ash hover:text-bone"
                                                    >
                                                        <Minus className="h-3 w-3" />
                                                    </button>
                                                    <span className="font-mono2 text-xs text-bone">{i.qty}</span>
                                                    <button
                                                        data-testid="cart-item-increase"
                                                        onClick={() => setQty(i.product.id, Math.min(i.qty + 1, i.product.stock))}
                                                        className="border border-red-950/60 p-1 text-ash hover:text-bone"
                                                    >
                                                        <Plus className="h-3 w-3" />
                                                    </button>
                                                </div>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        {items.length > 0 && (
                            <div className="border-t border-red-950/50 p-6">
                                {hasLiveAnimal && (
                                    <p className="mb-4 flex items-start gap-2 border border-emerald-900/50 bg-emerald-950/20 p-3 font-mono2 text-[10px] uppercase tracking-[0.15em] text-emerald-400">
                                        <Truck className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                        Live animals ship overnight air freight with live arrival guarantee. Freight quoted after order.
                                    </p>
                                )}
                                <div className="flex items-center justify-between font-mono2 text-sm">
                                    <span className="uppercase tracking-[0.2em] text-ash">Subtotal</span>
                                    <span className="text-lg text-bone" data-testid="cart-subtotal">
                                        ${subtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                    </span>
                                </div>
                                <div className="mb-4 border border-red-900/50 bg-black/40 p-4" data-testid="terms-gate">
                                    <a
                                        href="/terms"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="cart-terms-link"
                                        onClick={() => setTermsViewed(true)}
                                        className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-red-400 underline decoration-red-900 underline-offset-4 transition-colors duration-300 hover:text-red-300"
                                    >
                                        Read the Terms of Service
                                    </a>
                                    <label
                                        className={`mt-3 flex items-start gap-3 ${termsViewed ? "cursor-pointer" : "cursor-not-allowed opacity-40"}`}
                                    >
                                        <input
                                            type="checkbox"
                                            data-testid="cart-terms-checkbox"
                                            disabled={!termsViewed}
                                            checked={termsAccepted}
                                            onChange={(e) => setTermsAccepted(e.target.checked)}
                                            className="mt-0.5 accent-red-700"
                                        />
                                        <span className="text-[11px] leading-relaxed text-ash">
                                            I have read and accept the Terms of Service. I understand live
                                            arrival is guaranteed, and that if an animal dies after arrival I
                                            am responsible — no refunds or replacements after live arrival.
                                        </span>
                                    </label>
                                    {!termsViewed && (
                                        <p className="mt-2 font-mono2 text-[9px] uppercase tracking-[0.2em] text-ash/50">
                                            You must open the terms before you can accept
                                        </p>
                                    )}
                                </div>
                                {COMING_SOON ? (
                                    <p className="border border-red-900/50 bg-red-950/20 p-4 text-center font-mono2 text-[10px] uppercase tracking-[0.2em] text-red-400" data-testid="cart-coming-soon">
                                        Sales opening soon — checkout is not live yet
                                    </p>
                                ) : (
                                <button
                                    data-testid="cart-checkout-button"
                                    onClick={checkout}
                                    disabled={checkingOut || !termsAccepted}
                                    className="w-full bg-crimson py-4 font-mono2 text-xs uppercase tracking-[0.25em] text-bone transition-colors duration-300 hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-40"
                                >
                                    {checkingOut ? "Opening secure checkout…" : "Secure Checkout — Stripe"}
                                </button>
                                )}
                                <p className="mt-3 text-center font-mono2 text-[9px] uppercase tracking-[0.2em] text-ash/50">
                                    Encrypted payment · No account needed
                                </p>
                            </div>
                        )}
                    </motion.aside>
                </>
            )}
        </AnimatePresence>
    );
};
